<script lang="ts" setup>
import { useMyToast } from "@/composable/useMyToast";
import Button from "primevue/button"
import { Form, FormField } from "@primevue/forms";
import SrInputNumber from "./SrInputNumber.vue";
import { computed, nextTick, ref, watch } from "vue";
import { zodResolver } from "@primevue/forms/resolvers/zod";
import { z } from "zod"
import type { FormSubmitEvent } from "@primevue/forms";
import { i18n } from "@/i18n";
import { isNaN, isNull } from "lodash";
import { useTemplateRef } from "vue";
import type { FormInstance } from "@primevue/forms";
import { useTriggerSettings } from "@/composable/settings/useTriggerSettings";
import { srZodNumber } from "@/utils";

const { t } = i18n.global
const triggerForm = useTemplateRef<FormInstance>('triggerForm')
const { addToast } = useMyToast()
const initValues = { 'triggerSpread': null as number | null | undefined }

const root = computed(() => props.root)

const visibleTriggerValue = ref<number | null>(null)
const props = defineProps<{
  amount: number | null | undefined
  root: string | null
  cacheKey: string
}>()
const editTrigger = ref(false)
const { triggerValue } = useTriggerSettings(root, props.cacheKey)

const triggerResolver = zodResolver(z.object({
  triggerSpread: srZodNumber()
}))


const onTriggerFormSubmit = (e: FormSubmitEvent) => {
  if (!props.cacheKey || !props.root) {
    return
  }
  if (!e.valid) return
  const tried = Number(e.values.triggerSpread)
  if (e.values.triggerSpread && isNaN(tried)) {
    editTrigger.value = true
    addToast({
      severity: "error",
      message: t('modal.createSwapOrders.errors.invalidTriggerFormat')
    })
    return
  }
  if (!props.amount) {
    editTrigger.value = true
    addToast({
      severity: "error",
      message: t('modal.createSwapOrders.errors.triggerEmptyFormAbove')
    })
    return
  }
  let msg = 'modal.createSwapOrders.triggerSpreadSuccessfullySet'
  let newValue = e.values.triggerSpread ?? null
  if (newValue === '') {
    newValue = null
  }
  if (newValue !== null && newValue !== undefined) {
    triggerValue.value = newValue
  } else {
    triggerValue.value = null
    msg = 'modal.createSwapOrders.triggerSpreadSuccessfullyUnset'
  }
  editTrigger.value = false
  triggerValue.value = newValue
  addToast({
    severity: "success",
    message: t(msg)
  })
}

watch(editTrigger, async (v) => {
  if (v) {
    await nextTick()
    triggerForm.value?.setFieldValue('triggerSpread', visibleTriggerValue.value ?? triggerValue.value)
    if (visibleTriggerValue.value) {
      visibleTriggerValue.value = null
    }
  }
})

const triggerText = computed(() => {
  const triggerValueX = triggerValue.value
  if (isNull(triggerValueX)) {
    return t("modal.createSwapOrders.triggerEmpty")
  } else {
    return (t("modal.createSwapOrders.trigger") + ': ' + Number(triggerValueX).toFixed(2) + '%')
  }
})
defineExpose({ visibleTriggerValue })
</script>

<template>
  <div class="grid grid-cols-2">
    <Button fluid class="col-span-2" v-if="!Boolean(editTrigger)" icon="pi pi-cog"
      :variant="triggerValue === null ? 'outlined' : undefined" severity="success" @click="editTrigger = true"
      :label="triggerText">
    </Button>
    <div v-else class="col-span-2">
      <Form ref="triggerForm" :resolver="triggerResolver" @submit="onTriggerFormSubmit" :initial-values="initValues">
        <FormField name="triggerSpread" class="grid grid-cols-2 relative">
          <Button class="rounded-r-none" type="submit" severity="success" variant="outlined">
            <span class="hidden md:inline">{{ $t("modal.createSwapOrders.triggerEmpty") }}</span>
            <span class="md:hidden">{{ $t("modal.createSwapOrders.triggerWo") }}</span>
          </Button>
          <SrInputNumber class="rounded-l-none border-l-0" :placeholder="$t('modal.createSwapOrders.spread')" fluid
            variant="outlined" :step="0.1" :min="-200" :max="200" :maxFractionDigits="2">
          </SrInputNumber>
        </FormField>
      </Form>
    </div>
  </div>
</template>
